/* eslint-disable boundaries/element-types */
import {
  generateToggleFieldsFromFormFields,
  useGeckoForm,
} from 'components/form/formHelpers'
import {
  MeasurementApplication,
  PermissionsCheck,
} from 'components/form/formTypes'
import React, { forwardRef } from 'react'
import { FormProvider } from 'react-hook-form'
import { InputType } from 'types'

import { ToggleFormField } from './formFields/ToggleFormField'
import { FormComponentProps, StyledForm } from './GeckoForm'

interface GeckoToggleFormProps extends FormComponentProps {
  inEditMode: boolean
  inResolveMode?: boolean
  measurementApplication?: MeasurementApplication
  permissionsCheck?: PermissionsCheck
  resetOnSubmit?: boolean
}

export const GeckoToggleForm = forwardRef<
  HTMLFormElement,
  GeckoToggleFormProps
>(
  (
    {
      inEditMode,
      inResolveMode,
      measurementApplication,
      permissionsCheck,
      formFieldsByUniqueID,
      onSubmit,
      numberOfColumns = 2,
      propForm,
      resetOnSubmit,
    },
    ref
  ) => {
    const useForm = useGeckoForm(formFieldsByUniqueID)
    const form = propForm ?? useForm

    return (
      <FormProvider {...form}>
        <StyledForm
          $columns={numberOfColumns}
          onSubmit={form.handleSubmit((data) => {
            onSubmit(data)
            if (resetOnSubmit) {
              form.reset()
            }
          })}
          ref={ref}>
          {formFieldsByUniqueID.map((formFieldsByUniqueID) =>
            generateToggleFieldsFromFormFields(
              formFieldsByUniqueID.formFields,
              formFieldsByUniqueID.uniqueID,
              measurementApplication,
              permissionsCheck
            ).map((formField) => {
              let shouldShow: boolean | undefined = true
              if (formField.formDependencies) {
                shouldShow = false
                formField.formDependencies.forEach(({ name, has }) => {
                  const dependencyValue = form.watch(
                    `${name}#${formField.uniqueID}`
                  )
                  const dependencyInputFormField =
                    formFieldsByUniqueID.formFields.find(
                      (field) => field.dataKey === name
                    )
                  if (
                    dependencyInputFormField?.inputType ===
                    InputType.MultiSelect
                  ) {
                    if (dependencyValue?.includes(has.toString())) {
                      shouldShow = true
                    }
                  } else if (dependencyValue === has) {
                    shouldShow = true
                  }
                })
              }
              return (
                shouldShow && (
                  <ToggleFormField
                    key={formField.formItemKey}
                    formItemKey={formField.formItemKey}
                    formField={formField.formField}
                    fieldValue={formField.formFieldValue}
                    fieldUnits={formField.formFieldUnits}
                    canEditField={formField.canEditField}
                    canReadField={formField.canReadField}
                    units={formField.units}
                    formControl={form.control}
                    formErrors={form.formState.errors}
                    formTrigger={form.trigger}
                    formSetValue={form.setValue}
                    inEditMode={inEditMode}
                    inResolveMode={inResolveMode}
                  />
                )
              )
            })
          )}
        </StyledForm>
      </FormProvider>
    )
  }
)

GeckoToggleForm.displayName = 'GeckoToggleForm'
